import { Box, Button, Card, CardContent, IconButton, Typography } from '@mui/material';
import { memo } from 'react';
import { Link } from 'react-router-dom';
import { ServiceItem } from './ServiceItem';
import { ROUTE_KEYS, USDColor } from 'legacyfrontend/constants';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useFlightContext } from 'legacyfrontend/contexts/FlightDetailsContext';
import TooltipTotal from '../FlightCard/TooltipTotal';
import { CARD_WIDTH } from '.';
import { formatPrice } from 'legacyfrontend/helpers/formatPrice';

export const FareCard = memo(({ fare, baseFare, background, gray, text, disabled, currency }) => {
  const { selectFare } = useFlightContext();
  const isBaseFare = fare.id === baseFare?.id;
  const priceDifference = !isBaseFare ? fare.total - baseFare.total : 0;
  const farePrice = {
    base: fare?.base,
    totalTaxes: fare?.taxes,
    fee: fare?.total - fare?.amount,
  };

  const handleSaveCurrentUrl = () => {
    if (typeof window !== 'undefined') {
      const searchParams = window.location.search;

      try {
        sessionStorage.setItem('previousSearchUrl', searchParams);
      } catch (error) {
        console.warn('Unable to save previous URL to sessionStorage', error);
      }
    }
  };

  const handleReservar = () => {
    selectFare(fare);
    handleSaveCurrentUrl();
  };

  return (
    <Card
      className='card'
      sx={{
        border: isBaseFare ? 2 : 1,
        borderColor: isBaseFare ? background : gray,
        width: { xs: `${CARD_WIDTH - 50}px`, sm: `${CARD_WIDTH}px` },
        minHeight: '425px',
        position: 'relative',
      }}
      variant='outlined'
    >
      <CardContent sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <IconButton
          size='small'
          sx={{ alignSelf: 'flex-end', width: '1rem', height: '1rem' }}
          title={`Base: ${formatPrice(fare.base, currency, true)} + Taxes: ${formatPrice(
            fare.taxes,
            currency,
            true
          )}`}
        >
          <TooltipTotal currency={currency} iconFontSize='1rem' onlyIcon price={farePrice} />
        </IconButton>
        <Typography
          sx={{ fontSize: '2rem', wordBreak: 'break-all' }}
          title={formatPrice(fare.total, currency, true)}
          variant='h4'
        >
          {formatPrice(fare.total, currency, true)}
        </Typography>

        {!isBaseFare ? (
          <Typography sx={{ mb: 2, color: USDColor }} variant='subtitle2'>
            {`${priceDifference > 0 ? `+ ${formatPrice(priceDifference, currency, true)}` : `- ${formatPrice(priceDifference * -1, currency, true)}`} sobre tarifa base`}
          </Typography>
        ) : (
          <Typography sx={{ mb: 2, color: '#000', fontWeight: 800 }} variant='subtitle2'>
            Tarifa aplicada
          </Typography>
        )}

        {disabled ? (
          <Button
            disabled
            fullWidth
            sx={{
              mb: 2,
              backgroundColor: background,
              color: text,
              '&:hover': {
                opacity: 0.7,
                backgroundColor: background,
              },
            }}
            variant='contained'
          >
            Reservar
          </Button>
        ) : (
          <Link
            onClick={handleReservar}
            style={{ textDecoration: 'none' }}
            to={ROUTE_KEYS.BUSCAR_X_VUELOS_RESERVA}
          >
            <Button
              fullWidth
              sx={{
                mb: 2,
                backgroundColor: background,
                color: text,
                '&:hover': {
                  opacity: 0.7,
                  backgroundColor: background,
                },
              }}
              variant='contained'
            >
              Reservar
            </Button>
          </Link>
        )}

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            marginTop: 2,
            flex: 1,
          }}
        >
          {fare.services.map((service, index) => (
            <ServiceItem
              journeys={fare.journeys}
              key={`${service.code}-${index}`}
              service={service}
            />
          ))}
        </Box>

        {!!fare?.families[0] && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
              mt: 2,
              borderTop: `1px solid ${gray}`,
              pt: 2,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                color: 'text.secondary',
              }}
            >
              <ArrowForwardIcon fontSize='small' />
              <Typography variant='body2'>{fare?.families[0]}</Typography>
            </Box>
            {!!fare?.families[1] && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  color: 'text.secondary',
                }}
              >
                <ArrowBackIcon fontSize='small' />
                <Typography variant='body2'>{fare?.families[1]}</Typography>
              </Box>
            )}
          </Box>
        )}
      </CardContent>
    </Card>
  );
});
